<template>
  <page-view :menu="menu" :is-loading="show.screen" fill-height>
    <template #header>
      <base-header :title="$t('message.routesTitles.AntiDownload')" />
    </template>

    <template #content>
      <v-row no-gutters>
        <!-- title -->
        <v-col cols="12">
          <v-row align="center" no-gutters>
            <span class="pageTitle mb-6">
              {{ $t('message.routesTitles.AntiDownload') }}
              <sup class="pl-1 turquisePanda--text font-weight-normal">
                BETA
              </sup>
            </span>
          </v-row>
        </v-col>

        <!-- content -->
        <v-col cols="12">
          <v-row align="center" justify="start" no-gutters>
            <!-- content::main-text -->
            <v-col cols="9">
              <p v-html="$t('message.pages.AntiDownload.mainText')" />
            </v-col>

            <!-- content::alert -->
            <v-col cols="9" v-if="show.alert">
              <v-card elevation="0" align="center" max-width="300px" outlined>
                <v-card-text>
                  <p
                    v-if="message.status === 'fail'"
                    v-html="$t('message.pages.AntiDownload.fail')"
                    class="ma-0 danger-remove"
                  />
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-col>

        <!-- actions -->
        <v-col cols="12">
          <v-row align="center" justify="start" no-gutters>
            <v-col cols="12">
              <base-btn
                class="mt-5"
                :color="form.isBlocked ? 'red' : 'primary'"
                @click="show.dialog = !show.dialog"
              >
                <v-icon color="white"> mdi-shield-check </v-icon>
                <template v-if="form.isBlocked">
                  <span class="mx-2">
                    {{ $t('message.actions.disable') }}
                    
                  </span>
                </template>
                <template v-else>
                  <span class="mx-2">
                    {{ $t('message.actions.active') }}
                    
                  </span>
                </template>
              </base-btn>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <!-- dialog -->
      <v-dialog v-model="show.dialog" :persistent="loading" width="550px">
        <v-card dense>
          <!-- dialog::header -->
          <v-toolbar elevation="0">
            <v-toolbar-title
              class="text--subtitle primary--text"
              style="padding-left: 10px"
            >
              <v-icon color="primary" style="padding-bottom: 2px">
                {{
                  form.isBlocked ? 'mdi-shield-off-outline' : 'mdi-shield-check'
                }}
              </v-icon>
              <span>
                {{
                  form.isBlocked
                    ? `${$t('message.actions.disable')}`
                    : `${$t('message.actions.active')}`
                }}
                {{ $t('message.routesTitles.AntiDownload') }}
              </span>
            </v-toolbar-title>
            <v-spacer />
            <v-toolbar-items>
              <v-btn :disabled="loading" icon @click="close">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar-items>
          </v-toolbar>

          <!-- dialog::content -->
          <v-card-text v-if="isValidPlan" key="valid-plan" class="px-7 py-0">
            <p
              v-html="
                $t('message.pages.AntiDownload.dialogText', {
                  status: form.isBlocked
                    ? $t('message.pages.AntiDownload.statusActive')
                    : $t('message.pages.AntiDownload.statusDisabled'),
                  action: form.isBlocked
                    ? $t('message.pages.AntiDownload.buttonUnable')
                    : $t('message.pages.AntiDownload.buttonEnable'),
                })
              "
            />
          </v-card-text>
          <v-card-text v-else key="invalid-plan" class="px-7 py-0">
            <p
              v-html="
                validPlans.length === 1
                  ? $t('message.pages.AntiDownload.dialogTextInvalidPlan_one', {
                      plan: planInfo.subscription.name,
                      plans: validPlans[0],
                    })
                  : $t(
                      'message.pages.AntiDownload.dialogTextInvalidPlan_other',
                      {
                        plan: planInfo.subscription.name,
                        plans: validPlans.join(', '),
                      }
                    )
              "
            />
          </v-card-text>

          <!-- dialog::confirm -->
          <v-card-text
            v-if="isValidPlan && form.hasConfirmed"
            class="px-7 pt-3 pb-6"
          >
            <p class="font-weight-black">
              <span
                v-html="$t('message.pages.AntiDownload.dialogPasswordConfirm')"
              />
            </p>
            <v-text-field
              v-model.trim="form.password"
              class="mr-2"
              autocomplete="false"
              outlined
              dense
              :hide-details="!passwordErrorMessage"
              :disabled="loading"
              :placeholder="$t('message.pages.myAccount.currentPassword')"
              :append-icon="show.password ? 'mdi-eye' : 'mdi-eye-off'"
              :type="show.password ? 'text' : 'password'"
              :messages="passwordErrorMessage"
              :error="!!passwordErrorMessage"
              @keyup.enter="handleConfirmClick"
              @click:append="show.password = !show.password"
            >
              <template v-slot:message="{ message }">
                <span
                  v-if="passwordErrorMessage"
                  class="red--text caption align-text"
                >
                  <v-icon color="red" size="18">mdi-alert-circle</v-icon>
                  <span>{{ message }}</span>
                </span>
              </template>
            </v-text-field>
          </v-card-text>

          <!-- dialog::actions -->
          <v-card-actions class="d-flex justify-end px-7 pb-6">
            <!-- actions -->
            <template v-if="isValidPlan">
              <!-- close -->
              <base-btn
                :disabled="loading"
                class="mr-3"
                type="secondary"
                @click="close"
              >
                {{ $t('message.pages.AntiDownload.buttonCancel') }}
              </base-btn>

              <!-- active -->
              <base-btn
                v-if="!form.hasConfirmed"
                :color="form.isBlocked ? 'red' : 'primary'"
                :disabled="loading"
                :loading="loading"
                @click="
                  () => {
                    if (form.isBlocked) form.hasConfirmed = true
                    else submit({ bypass: true })
                  }
                "
              >
                {{
                  form.isBlocked
                    ? $t('message.pages.AntiDownload.buttonUnable')
                    : $t('message.pages.AntiDownload.buttonEnable')
                }}
              </base-btn>

              <!-- confirm -->
              <base-btn
                v-if="form.hasConfirmed"
                :disabled="loading"
                :loading="loading"
                @click="submit"
              >
                {{ $t('message.pages.AntiDownload.buttonConfirm') }}
              </base-btn>
            </template>

            <!-- upgrade -->
            <base-btn
              v-else
              color="primary"
              @click="$router.push({ name: 'Cart' })"
            >
              {{ $t('message.pages.AntiDownload.dialogUpgrade') }}
            </base-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </page-view>
</template>

<script>
// external imports

// internal imports
import { mapGetters, mapActions, mapState } from 'vuex'

// API
import blockDownloadApi from '@/services/blockDownloadApi'

// Components
import PageView from '@/layout/PageView.vue'
import BaseBtn from '@/components/BaseBtn.vue'

export default {
  name: 'AntiDownload',
  components: {
    PageView,
    BaseBtn,
    BaseHeader: () => import('@/components/BaseHeader.vue'),
  },
  data: () => ({
    loading: false,
    form: {
      isBlocked: null,
      oldStatus: null,
      hasConfirmed: false,
      password: '',
    },
    errors: {
      password: {
        minError: false,
        notSafeError: false,
        invalidPassword: false,
        limitExceeded: false,
        expiredCode: false,
        invalidOldPass: false,
        invalidAccountError: false,
        unexpectedError: false,
      },
      unknownError: null,
    },
    message: {
      status: '',
    },
    show: {
      alert: false,
      screen: false,
      dialog: false,
      password: false,
    },
    validPlans: ['Gold'],
  }),
  computed: {
    ...mapState({
      planInfo: (state) => state.user.userPlanInfo,
    }),
    ...mapGetters({
      userAttributes: 'session/userAttributes',
    }),
    menu() {
      return this.$store.state.user.securityNavItems
    },
    passwordErrorMessage() {
      const i18n = this.$i18next
      let message = ''

      if (this.errors.password.invalidOldPass) {
        message = i18n.t('message.invalidPassword')
      }

      if (this.errors.password.minError) {
        message = i18n.t('message.errors.minError')
      }

      if (
        this.errors.password.notSafeError ||
        this.errors.password.invalidPassword
      ) {
        message = i18n.t('message.errors.notSafeError')
      }

      if (this.errors.password.limitExceeded) {
        message = i18n.t('message.errors.limitExceeded')
      }

      if (this.errors.password.expiredCode) {
        message = i18n.t('message.errors.expiredCode')
      }

      if (this.errors.password.unexpectedError) {
        message = `${i18n.t('message.errors.unexpectedError')} ${
          this.errors.unknownError
        }`
      }

      if (this.errors.password.invalidAccountError) {
        message = i18n.t(
          'message.pages.video.deleteModal.errors.invalidAccountError'
        )
      }

      if (this.errors.password.internalErrorException) {
        message = i18n.t('message.pages.video.deleteModal.errors.internalError')
      }

      return message
    },
    oldStatus() {
      const i18n = this.$i18next
      return this.form.oldStatus === true
        ? i18n.t('message.pages.AntiDownload.statusActive')
        : i18n.t('message.pages.AntiDownload.statusDisabled')
    },
    newStatus() {
      const i18n = this.$i18next
      return this.form.isBlocked === true
        ? i18n.t('message.pages.AntiDownload.statusActive')
        : i18n.t('message.pages.AntiDownload.statusDisabled')
    },
    isValidPlan() {
      const { name } = this.planInfo?.subscription || {}
      return this.validPlans.includes(name)
    },
  },
  watch: {
    'show.dialog'(value) {
      if (value) {
        this.show.alert = false
        this.message.status = ''
      } else this.show.alert = !!this.message.status
    },
  },
  async mounted() {
    this.$root.$emit('changeNavItem', 2)
    await this.getBlock()
  },
  methods: {
    ...mapActions('session', ['signInUser']),
    close() {
      this.form.password = ''
      this.form.hasConfirmed = false

      this.setData(this.show, false)
      this.setData(this.errors.password, false)
    },
    setData(obj, value) {
      Object.keys(obj).forEach((e) => (obj[e] = value))
    },

    async confirmUserPassword() {
      let status = false

      try {
        await this.signInUser({
          username: this.userAttributes.email,
          password: this.form.password.trim(),
        })

        status = true
      } catch (error) {
        if (!error.code) {
          const anyError = JSON.parse(JSON.stringify(error))

          if (anyError.message.includes('Network Error')) {
            this.errors.password.unexpectedError = true
            this.errors.unknownError = 'Code: 500'

            return status
          }
        }

        if (
          error.code === 'NotAuthorizedException' ||
          error.code === 'InvalidParameterException'
        ) {
          this.errors.password.invalidAccountError = true
        }

        if (error.code === 'InternalErrorException') {
          this.errors.password.internalErrorException = true
        }
      }

      return status
    },

    async submit({ bypass = false } = {}) {
      this.loading = true

      this.setData(this.errors.password, false)

      if (!bypass) {
        if (this.form.password === '') {
          this.errors.password.notSafeError = true
          this.loading = false
          return
        }

        if (this.form.password.length < 8) {
          this.errors.password.minError = true
          this.loading = false
          return
        }

        const isValid = await this.confirmUserPassword()
        if (!isValid) return (this.loading = false)
      }

      try {
        await this.updateBlock()
        this.close()
      } catch (error) {
        console.error(error)

        if (error.code === 'NotAuthorizedException') {
          this.errors.password.invalidOldPass = true
        }
        if (error.code === 'LimitExceededException') {
          this.errors.password.limitExceeded = true
        }
      } finally {
        this.loading = false
      }
    },
    async getBlock() {
      this.show.screen = true

      try {
        const { data } = await blockDownloadApi.getBlockDownload()

        this.form.isBlocked = data.status ?? false
        this.form.oldStatus = this.form.isBlocked
      } catch (error) {
        console.log('error::get-block', error)
      } finally {
        this.show.screen = false
      }
    },
    async updateBlock() {
      this.loading = true
      this.form.oldStatus = this.form.isBlocked

      const status = !this.form.isBlocked

      try {
        const { data } = await blockDownloadApi.updateBlockDownload({ status })
        this.form.isBlocked = data.status
      } catch (error) {
        if (this.passwordErrorMessage === '') this.message.status = 'fail'
        console.log('error::update-block', error)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
$size: 1.35rem;
.pageTitle {
  font-weight: 500;
  font-size: 18px;
  line-height: 21px;
  color: var(--v-brand-darken2);
}

.cardTitle {
  font-weight: 600;
  font-size: 24px;
  line-height: 36px;
}

.propertiesTitle {
  font-style: normal;
  font-weight: normal;
  font-size: 17px;
  line-height: 21px;
  letter-spacing: 0.05em;
}

.truncateText {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.greyBackground {
  background-color: #6f6f6f;
}

.intro {
  &__btn {
    --intro-background-color: rgba(255, 255, 255, 0.54);
    --intro-foreground-color: #000;
  }
}

.intro {
  &__btn {
    --intro-background-color: var(--v-neutral-lighten1);
    --intro-foreground-color: var(--v-neutral-lighten4);

    position: relative;
    top: 0.5px;

    &::v-deep {
      .v-icon {
        background-color: var(--intro-background-color);
        width: $size;
        height: $size;
        border-radius: 100%;

        &[class*='mdi-'] {
          font-size: $size * 0.8;

          &::before {
            color: var(--intro-foreground-color);
          }
        }
      }
    }
  }
}
</style>
